import { useMemo } from 'react'

import type { ObPlanId } from '../../../gql/graphql'

import { OBPlanIdValues } from '../types'
import type { ExtendedPlanOption } from '../utils/dataTransformers'
import {
  calculateRecommendedPlan,
  getQuickRecommendation,
} from '../utils/planRecommendation'
import type { RecommendationFactors } from '../utils/planRecommendation'
import { useAccount } from '../../../hooks/useAccount'

export interface UsePlanRecommendationProps {
  /** Available plans with pricing calculations */
  availablePlans: ExtendedPlanOption[]
  /** Current user's plan ID */
  currentPlanId: ObPlanId
  /** Number of channels selected */
  channelCount: number
  /** Whether user has ever been a paying customer */
  hasEverBeenPayingCustomer?: boolean
}

/**
 * Custom hook for calculating the recommended plan
 * Returns only the recommended plan ID
 */
export function usePlanRecommendation({
  availablePlans,
  currentPlanId,
  channelCount,
  hasEverBeenPayingCustomer = false,
}: UsePlanRecommendationProps): {
  recommendedPlanId: ObPlanId
} {
  const { account } = useAccount()
  const userCount = account?.currentOrganization?.members?.totalCount ?? 1

  const recommendedPlanId = useMemo(() => {
    // If we don't have pricing data, use quick heuristics
    if (!availablePlans || availablePlans.length === 0) {
      const needsAdvancedAnalytics =
        hasEverBeenPayingCustomer ||
        channelCount > 3 ||
        currentPlanId !== OBPlanIdValues.free

      return getQuickRecommendation(
        channelCount,
        userCount,
        needsAdvancedAnalytics,
      )
    }

    const factors: RecommendationFactors = {
      channelCount,
      userCount,
      currentPlanId,
      needsAdvancedAnalytics:
        hasEverBeenPayingCustomer ||
        channelCount > 3 ||
        currentPlanId !== OBPlanIdValues.free,
      needsTeamFeatures: userCount > 1 || currentPlanId === OBPlanIdValues.team,
    }

    const result = calculateRecommendedPlan(availablePlans, factors)
    return result.recommendedPlanId
  }, [
    availablePlans,
    channelCount,
    userCount,
    hasEverBeenPayingCustomer,
    currentPlanId,
  ])

  return {
    recommendedPlanId,
  }
}
